import React, { useEffect, useMemo, useRef, useState } from "react";
import TitleButtons from "../../../components/TitleButtons";
import TextField from "../../../components/TextField";
import DropField from "../../../components/DropField";

const API_URL =
  "https://api.eva-googlemarine.com/departamentos/suprimentos/produtos";

const ALLOWED_CURRENCIES = ["BRL", "USD", "EUR"] as const;
type AllowedCurrency = (typeof ALLOWED_CURRENCIES)[number];
type NoticeType = "success" | "error" | "notify";
type ScanMode = "idle" | "manual" | "scanner";

type EntryStatus =
  | "ready"
  | "manual"
  | "blocked"
  | "hidden"
  | "submitting"
  | "submitted"
  | "failed";

type EntryItem = {
  id: string;
  codigo: string;
  nome: string;
  quantidade: string;
  custo: string;
  moeda: AllowedCurrency;

  ncm: string;
  classificacao: string;
  posicao: string;
  dimensao: string;
  peso: string;

  exists: boolean;
  expanded: boolean;
  status: EntryStatus;
  statusText: string;

  isOculto: boolean;
  isBlockedByDept: boolean;
  blockedDeptName: string | null;
};

function sanitizeuidToId(uid: unknown): string {
  const pic = String(uid ?? "");
  return pic.match(/\d+/)?.[0] ?? "";
}

function parseWeightToKg(raw: string): string | null {
  if (!raw) return null;
  const s = raw.trim().toLowerCase();
  const hasKg = /k\s*g/.test(s);
  const hasG = /(^|\s|\d)g(\s|$)/.test(s) && !hasKg;

  const numStr = s.replace(/[^0-9.,+-]/g, "").replace(/,/g, ".");
  const n = parseFloat(numStr);
  if (!isFinite(n) || n <= 0) return null;

  const kg = hasG ? n / 1000 : n;
  return Number(kg.toFixed(3)).toString();
}

function toApiMoney(raw: string): string {
  const s = String(raw ?? "").trim();
  if (!s) return "0.00";

  const clean = s.replace(/[^0-9.,-]/g, "");
  const lastComma = clean.lastIndexOf(",");
  const lastDot = clean.lastIndexOf(".");
  const decPos = Math.max(lastComma, lastDot);

  if (decPos === -1) {
    const intPart = clean.replace(/\D/g, "") || "0";
    return `${intPart}.00`;
  }

  const intPart = clean.slice(0, decPos).replace(/\D/g, "") || "0";
  let fracPart = clean.slice(decPos + 1).replace(/\D/g, "");

  if (fracPart.length > 2) {
    const n = Number(`${intPart}.${fracPart || "0"}`);
    return n.toFixed(2);
  }

  if (fracPart.length === 1) fracPart += "0";
  if (fracPart.length === 0) fracPart = "00";

  return `${intPart}.${fracPart}`;
}

function isValidApiMoney(s: string): boolean {
  return /^-?\d+(\.\d{2})$/.test(s);
}

function getCurrencyLocale(currency: AllowedCurrency): string {
  switch (currency) {
    case "USD":
      return "en-US";
    case "EUR":
      return "pt-BR";
    case "BRL":
    default:
      return "pt-BR";
  }
}

function createManualItem(codigo: string): EntryItem {
  return {
    id: crypto.randomUUID(),
    codigo: codigo.toUpperCase(),
    nome: "",
    quantidade: "1",
    custo: "",
    moeda: "BRL",
    ncm: "",
    classificacao: "",
    posicao: "",
    dimensao: "",
    peso: "",
    exists: false,
    expanded: true,
    status: "manual",
    statusText: "NOVO PRODUTO - PREENCHA OS DADOS PRINCIPAIS",
    isOculto: false,
    isBlockedByDept: false,
    blockedDeptName: null,
  };
}

const opcoesClassificacao = [
  { value: "ESTOQUE", label: "ESTOQUE" },
  { value: "CONSUMO", label: "CONSUMO" },
  { value: "RH", label: "RH" },
  { value: "VENDA", label: "VENDA" },
];

const opcoesMoeda = [
  { value: "BRL", label: "BRL" },
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" },
];

const opcoesPosicao = [
  { value: "A1", label: "A1" },
  { value: "A2", label: "A2" },
  { value: "A3", label: "A3" },
  { value: "A4", label: "A4" },
  { value: "A5", label: "A5" },
  { value: "A6", label: "A6" },
  { value: "B1", label: "B1" },
  { value: "B2", label: "B2" },
  { value: "B3", label: "B3" },
  { value: "B4", label: "B4" },
  { value: "B5", label: "B5" },
  { value: "B6", label: "B6" },
  { value: "C1", label: "C1" },
  { value: "C2", label: "C2" },
  { value: "C3", label: "C3" },
  { value: "C4", label: "C4" },
  { value: "C5", label: "C5" },
  { value: "C6", label: "C6" },
  { value: "D1", label: "D1" },
  { value: "D2", label: "D2" },
  { value: "D3", label: "D3" },
  { value: "D4", label: "D4" },
  { value: "D5", label: "D5" },
  { value: "D6", label: "D6" },
  { value: "E1", label: "E1" },
  { value: "E2", label: "E2" },
  { value: "E3", label: "E3" },
  { value: "E4", label: "E4" },
  { value: "E5", label: "E5" },
  { value: "E6", label: "E6" },
  { value: "F1", label: "F1" },
  { value: "F2", label: "F2" },
  { value: "F3", label: "F3" },
  { value: "F4", label: "F4" },
  { value: "F5", label: "F5" },
  { value: "F6", label: "F6" },
  { value: "G1", label: "G1" },
  { value: "G2", label: "G2" },
  { value: "G3", label: "G3" },
  { value: "G4", label: "G4" },
  { value: "G5", label: "G5" },
  { value: "G6", label: "G6" },
  { value: "H1", label: "H1" },
  { value: "H2", label: "H2" },
  { value: "H3", label: "H3" },
  { value: "H4", label: "H4" },
  { value: "H5", label: "H5" },
  { value: "H6", label: "H6" },
  { value: "I1", label: "I1" },
  { value: "I2", label: "I2" },
  { value: "I3", label: "I3" },
  { value: "I4", label: "I4" },
  { value: "I5", label: "I5" },
  { value: "I6", label: "I6" },
  { value: "J1", label: "J1" },
  { value: "J2", label: "J2" },
  { value: "J3", label: "J3" },
  { value: "J4", label: "J4" },
  { value: "J5", label: "J5" },
  { value: "J6", label: "J6" },
  { value: "CENTRAL 1", label: "CENTRAL 1" },
  { value: "CENTRAL 2", label: "CENTRAL 2" },
  { value: "CENTRAL 3", label: "CENTRAL 3" },
  { value: "CENTRAL 4", label: "CENTRAL 4" },
  { value: "CENTRAL 5", label: "CENTRAL 5" },
  { value: "CENTRAL 6", label: "CENTRAL 6" },
  { value: "CENTRAL 7", label: "CENTRAL 7" },
  { value: "CENTRAL 8", label: "CENTRAL 8" },
];

const NoticeBanner: React.FC<{
  notice: { type: NoticeType; text: string };
  onClose?: () => void;
}> = ({ notice, onClose }) => {
  let bg = "#eef4ff";
  let color = "#1f3b5b";
  let border = "#b7cbe3";

  if (notice.type === "success") {
    bg = "#edf7ed";
    color = "#234b2c";
    border = "#b8d5bb";
  } else if (notice.type === "error") {
    bg = "#fff1f1";
    color = "#6b1f1f";
    border = "#e0b4b4";
  }

  return (
    <div
      style={{
        margin: "12px 0 16px 0",
        padding: "10px 12px",
        background: bg,
        color,
        border: `1px solid ${border}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>
          {notice.text}
        </span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              cursor: "pointer",
              fontSize: 15,
              padding: 0,
            }}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

const RegistrarProdutoEntradaEstoque: React.FC = () => {
  const [scanCode, setScanCode] = useState("");
  const [items, setItems] = useState<EntryItem[]>([]);
  const [notice, setNotice] = useState<null | {
    type: NoticeType;
    text: string;
  }>(null);
  const [responsibleId, setResponsibleId] = useState("");
  const [empresaId, setEmpresaId] = useState("");
  const [loadingCode, setLoadingCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scanMode, setScanMode] = useState<ScanMode>("idle");

  const scanInputRef = useRef<HTMLInputElement | null>(null);

  const scannerBufferRef = useRef("");
  const scannerTimesRef = useRef<number[]>([]);
  const scannerTimerRef = useRef<number | null>(null);
  const suppressNextChangeRef = useRef(false);

  const SCANNER_MIN_LENGTH = 6;
  const SCANNER_MAX_AVG_INTERVAL = 35;
  const SCANNER_IDLE_COMMIT_MS = 70;

  useEffect(() => {
    fetch("https://api.eva-googlemarine.com/auth/me", {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    })
      .then((r) => r.json())
      .then((data) => {
        const rawResp = (data?.sessao?.id ??
          data?.sessao?.usuario_id ??
          sanitizeuidToId(data?.sessao?.uid)) as any;

        const asRespNum = Number(rawResp);
        if (Number.isFinite(asRespNum) && asRespNum > 0) {
          setResponsibleId(String(asRespNum));
        } else {
          setNotice({
            type: "notify",
            text: "NÃO FOI POSSÍVEL IDENTIFICAR O RESPONSÁVEL NA SESSÃO ATUAL",
          });
        }

        const rawEmpresa = (data?.sessao?.empresa_id ??
          data?.sessao?.empresaId ??
          data?.sessao?.empresa ??
          "1") as any;

        const asEmpresaNum = Number(rawEmpresa);
        if (Number.isFinite(asEmpresaNum) && asEmpresaNum > 0) {
          setEmpresaId(String(asEmpresaNum));
        } else {
          setEmpresaId("1");
        }
      })
      .catch(() => {
        setNotice({
          type: "error",
          text: "FALHA AO CONSULTAR /AUTH/ME. VERIFIQUE A SESSÃO",
        });
        setEmpresaId("1");
      });
  }, []);

  function focusScanInput() {
    requestAnimationFrame(() => {
      scanInputRef.current?.focus();
      scanInputRef.current?.select();
    });
  }

  function normalizeCode(value: string) {
    return String(value ?? "")
      .toUpperCase()
      .replace(/\s+/g, "")
      .trim();
  }

  function resetScannerTracking(nextMode: ScanMode = "idle") {
    scannerBufferRef.current = "";
    scannerTimesRef.current = [];

    if (scannerTimerRef.current) {
      window.clearTimeout(scannerTimerRef.current);
      scannerTimerRef.current = null;
    }

    setScanMode(nextMode);
  }

  function getAverageInterval(times: number[]) {
    if (times.length < 2) return Infinity;

    let total = 0;
    for (let i = 1; i < times.length; i += 1) {
      total += times[i] - times[i - 1];
    }

    return total / (times.length - 1);
  }

  function updateItem(itemId: string, patch: Partial<EntryItem>) {
    setItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, ...patch } : item))
    );
  }

  function removeItem(itemId: string) {
    setItems((prev) => prev.filter((item) => item.id !== itemId));
    focusScanInput();
  }

  function toggleExpanded(itemId: string) {
    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, expanded: !item.expanded } : item
      )
    );
  }

  function getItemByCode(codigo: string) {
    return items.find(
      (item) => item.codigo.trim().toUpperCase() === codigo.trim().toUpperCase()
    );
  }

  function getScanModeLabel() {
    switch (scanMode) {
      case "scanner":
        return "LEITOR DETECTADO";
      case "manual":
        return "DIGITAÇÃO MANUAL";
      case "idle":
      default:
        return "AGUARDANDO LEITURA";
    }
  }

  function getScanModeStyle() {
    switch (scanMode) {
      case "scanner":
        return {
          background: "#edf7ed",
          color: "#234b2c",
          border: "#b8d5bb",
        };
      case "manual":
        return {
          background: "#eef4ff",
          color: "#1f3b5b",
          border: "#b7cbe3",
        };
      case "idle":
      default:
        return {
          background: "#f3f4f6",
          color: "#4b5563",
          border: "#d1d5db",
        };
    }
  }

  async function handleAddCode() {
    await handleAddCodeWithValue(scanCode, "manual");
  }

  async function handleAddCodeWithValue(
    rawValue?: string,
    mode: ScanMode = "manual"
  ) {
    setNotice(null);

    const codigo = normalizeCode(rawValue ?? scanCode);
    if (!codigo) return;

    const existing = getItemByCode(codigo);
    if (existing) {
      const currentQty = Number(existing.quantidade || "0");
      updateItem(existing.id, {
        quantidade: String(currentQty > 0 ? currentQty + 1 : 1),
        statusText:
          mode === "scanner"
            ? "QUANTIDADE INCREMENTADA POR LEITURA"
            : "QUANTIDADE INCREMENTADA NESTE LOTE",
      });
      setScanCode("");
      focusScanInput();
      resetScannerTracking("idle");
      return;
    }

    setLoadingCode(true);

    try {
      const res = await fetch(
        `https://api.eva-googlemarine.com/departamentos/suprimentos/produtos/${encodeURIComponent(
          codigo
        )}`,
        {
          method: "GET",
          headers: { Accept: "application/json" },
        }
      );

      const data = await res.json().catch(() => ({} as any));

      if (
        data &&
        data.ok === false &&
        typeof data.erro === "string" &&
        data.erro.trim().toLowerCase() ===
          "esse produto possui registro em outro departamento" &&
        data.departamento
      ) {
        const blockedItem: EntryItem = {
          ...createManualItem(codigo),
          expanded: false,
          status: "blocked",
          statusText: `BLOQUEADO - VINCULADO AO DEPARTAMENTO ${String(
            data.departamento
          ).toUpperCase()}`,
          isBlockedByDept: true,
          blockedDeptName: String(data.departamento).toUpperCase(),
        };

        setItems((prev) => [blockedItem, ...prev]);
        setScanCode("");
        focusScanInput();
        resetScannerTracking("idle");
        return;
      }

      if (!res.ok || !data?.ok) {
        setItems((prev) => [createManualItem(codigo), ...prev]);
        setScanCode("");
        focusScanInput();
        resetScannerTracking("idle");
        return;
      }

      const produto = data.data?.produto ?? {};
      const estoque = data.data?.estoque_atual ?? null;
      const custos: any[] = data.data?.custos ?? [];

      let custo = "";
      if (estoque?.custo_unitario_atual) {
        custo = String(estoque.custo_unitario_atual);
      } else if (custos.length && custos[0]?.custo_unitario) {
        custo = String(custos[0].custo_unitario);
      }

      const moedaDetectada =
        (estoque && estoque.moeda) || (produto && produto.moeda) || "BRL";

      const oculto = Boolean(data?.oculto ?? data?.data?.produto?.ocultar);

      const loadedItem: EntryItem = {
        id: crypto.randomUUID(),
        codigo,
        nome: produto.nome ?? "",
        quantidade: "1",
        custo: custo ? custo.replace(".", ",") : "",
        moeda: ALLOWED_CURRENCIES.includes(moedaDetectada)
          ? (moedaDetectada as AllowedCurrency)
          : "BRL",
        ncm: produto.ncm ?? "",
        classificacao: (produto.classificacao ?? "").toString().toUpperCase(),
        posicao: produto.posicao ?? "",
        dimensao: produto.dimensao ?? "",
        peso: produto.peso ?? "",
        exists: true,
        expanded: false,
        status: oculto ? "hidden" : "ready",
        statusText: oculto
          ? "PRODUTO OCULTO - NÃO PODE SER UTILIZADO"
          : mode === "scanner"
          ? "ITEM CARREGADO POR LEITURA - AJUSTE QUANTIDADE E CUSTO SE NECESSÁRIO"
          : "ITEM CARREGADO - AJUSTE QUANTIDADE E CUSTO SE NECESSÁRIO",
        isOculto: oculto,
        isBlockedByDept: false,
        blockedDeptName: null,
      };

      setItems((prev) => [loadedItem, ...prev]);
      setScanCode("");
      focusScanInput();
      resetScannerTracking("idle");
    } catch (e: any) {
      setNotice({
        type: "error",
        text: `FALHA AO VERIFICAR CÓDIGO: ${e?.message || "ERRO DE REDE"}`,
      });
    } finally {
      setLoadingCode(false);
    }
  }

  function scheduleScannerCommit() {
    if (scannerTimerRef.current) {
      window.clearTimeout(scannerTimerRef.current);
    }

    scannerTimerRef.current = window.setTimeout(() => {
      const code = normalizeCode(scannerBufferRef.current);
      const avgInterval = getAverageInterval(scannerTimesRef.current);

      const looksLikeScanner =
        code.length >= SCANNER_MIN_LENGTH &&
        avgInterval <= SCANNER_MAX_AVG_INTERVAL;

      if (looksLikeScanner) {
        setScanMode("scanner");
        void handleAddCodeWithValue(code, "scanner");
        return;
      }

      if (code) {
        setScanMode("manual");
      } else {
        setScanMode("idle");
      }

      resetScannerTracking(code ? "manual" : "idle");
    }, SCANNER_IDLE_COMMIT_MS);
  }

  function handleScanInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const isSingleChar =
      e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

    if (e.key === "Enter") {
      e.preventDefault();

      const candidate = normalizeCode(scannerBufferRef.current || scanCode);
      if (candidate) {
        void handleAddCodeWithValue(
          candidate,
          scanMode === "scanner" ? "scanner" : "manual"
        );
      }

      resetScannerTracking("idle");
      return;
    }

    if (e.key === "Backspace" || e.key === "Delete") {
      setScanMode("manual");
      resetScannerTracking("manual");
      return;
    }

    if (!isSingleChar) return;

    const now = performance.now();

    scannerBufferRef.current += e.key.toUpperCase();
    scannerTimesRef.current.push(now);

    setScanMode("manual");
    scheduleScannerCommit();
  }

  function handleScanInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = normalizeCode(e.target.value);

    if (suppressNextChangeRef.current) {
      suppressNextChangeRef.current = false;
      return;
    }

    setScanCode(value);

    if (value) {
      setScanMode("manual");
    } else {
      setScanMode("idle");
    }
  }

  function handleScanInputPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();

    const pasted = normalizeCode(e.clipboardData.getData("text"));
    if (!pasted) return;

    suppressNextChangeRef.current = true;
    setScanCode(pasted);
    setScanMode("scanner");

    resetScannerTracking("scanner");
    void handleAddCodeWithValue(pasted, "scanner");
  }

  function validateItem(item: EntryItem): string | null {
    if (!item.codigo.trim()) return "CÓDIGO NÃO INFORMADO";
    if (item.isOculto) return "PRODUTO OCULTO";
    if (item.isBlockedByDept) {
      return `PRODUTO VINCULADO A ${
        item.blockedDeptName ?? "OUTRO DEPARTAMENTO"
      }`;
    }
    if (!item.nome.trim()) return "NOME DO PRODUTO NÃO INFORMADO";
    if (!item.custo.trim()) return "CUSTO UNITÁRIO NÃO INFORMADO";
    if (!item.quantidade.trim() || isNaN(Number(item.quantidade))) {
      return "QUANTIDADE INVÁLIDA";
    }
    if (Number(item.quantidade) <= 0) {
      return "QUANTIDADE DEVE SER MAIOR QUE ZERO";
    }

    const money = toApiMoney(item.custo);
    if (!isValidApiMoney(money)) return "CUSTO UNITÁRIO INVÁLIDO";

    if (item.peso.trim() && !parseWeightToKg(item.peso)) {
      return "PESO INVÁLIDO";
    }

    return null;
  }

  async function handleSubmit() {
    setNotice(null);

    if (!items.length) {
      setNotice({
        type: "notify",
        text: "NENHUM ITEM FOI ADICIONADO",
      });
      return;
    }

    if (!responsibleId || isNaN(Number(responsibleId))) {
      setNotice({
        type: "error",
        text: "RESPONSÁVEL INVÁLIDO. VERIFIQUE A SESSÃO",
      });
      return;
    }

    let hasError = false;

    setItems((prev) =>
      prev.map((item) => {
        const error = validateItem(item);

        if (error) {
          hasError = true;
          return {
            ...item,
            status: "failed",
            statusText: error,
            expanded: true,
          };
        }

        return {
          ...item,
          status: "ready",
          statusText: "PRONTO PARA REGISTRO",
        };
      })
    );

    if (hasError) {
      setNotice({
        type: "error",
        text: "EXISTEM ITENS COM ERRO. CORRIJA ANTES DE REGISTRAR",
      });
      return;
    }

    setSubmitting(true);

    let successCount = 0;
    let failCount = 0;

    for (const item of items) {
      const error = validateItem(item);
      if (error) continue;

      setItems((prev) =>
        prev.map((row) =>
          row.id === item.id
            ? {
                ...row,
                status: "submitting",
                statusText: "REGISTRANDO...",
              }
            : row
        )
      );

      try {
        const payload = {
          codigo: item.codigo.trim().toUpperCase(),
          nome: item.nome.trim() || null,
          ncm: item.ncm.trim() || null,
          classificacao: item.classificacao.trim() || null,
          posicao: item.posicao.trim() || null,
          dimensao: item.dimensao.trim() || null,
          peso: item.peso.trim() ? parseWeightToKg(item.peso) : null,
          custo_unitario: toApiMoney(item.custo),
          moeda: item.moeda,
          quantidade: Number(item.quantidade),
          responsavel_registro: Number(responsibleId),
          empresa_id: Number(empresaId || "1"),
        };

        const res = await fetch(API_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok || data?.ok === false) {
          const backendMsg =
            data?.erro ||
            (Array.isArray(data?.erros) && data.erros.join(" | ")) ||
            data?.message ||
            "FALHA AO REGISTRAR";

          failCount += 1;

          setItems((prev) =>
            prev.map((row) =>
              row.id === item.id
                ? {
                    ...row,
                    status: "failed",
                    statusText: String(backendMsg).toUpperCase(),
                    expanded: true,
                  }
                : row
            )
          );
          continue;
        }

        successCount += 1;

        setItems((prev) =>
          prev.map((row) =>
            row.id === item.id
              ? {
                  ...row,
                  status: "submitted",
                  statusText: "REGISTRADO COM SUCESSO",
                }
              : row
          )
        );
      } catch (err: any) {
        failCount += 1;

        setItems((prev) =>
          prev.map((row) =>
            row.id === item.id
              ? {
                  ...row,
                  status: "failed",
                  statusText: `ERRO DE REDE: ${String(
                    err?.message || "TENTE NOVAMENTE"
                  ).toUpperCase()}`,
                  expanded: true,
                }
              : row
          )
        );
      }
    }

    setSubmitting(false);

    if (successCount && !failCount) {
      setNotice({
        type: "success",
        text: `REGISTRO CONCLUÍDO COM SUCESSO - ${successCount} ITEM(NS) PROCESSADO(S)`,
      });
    } else if (successCount && failCount) {
      setNotice({
        type: "notify",
        text: `PROCESSAMENTO PARCIAL - ${successCount} ITEM(NS) REGISTRADO(S) E ${failCount} COM FALHA`,
      });
    } else {
      setNotice({
        type: "error",
        text: "NENHUM ITEM FOI REGISTRADO",
      });
    }

    focusScanInput();
  }

  const summary = useMemo(() => {
    const blockedItems = items.filter(
      (item) => item.isBlockedByDept || item.status === "blocked"
    );

    const validItems = items.filter(
      (item) => !(item.isBlockedByDept || item.status === "blocked")
    );

    const totalItens = validItems.length;
    const totalQtd = validItems.reduce(
      (acc, item) => acc + (Number(item.quantidade) || 0),
      0
    );

    const totalItensBloqueados = blockedItems.length;
    const totalQtdBloqueada = blockedItems.reduce(
      (acc, item) => acc + (Number(item.quantidade) || 0),
      0
    );

    return {
      totalItens,
      totalQtd,
      totalItensBloqueados,
      totalQtdBloqueada,
    };
  }, [items]);

  function getRowBackground(item: EntryItem) {
    switch (item.status) {
      case "blocked":
      case "hidden":
      case "failed":
        return "#fff8f8";
      case "submitted":
        return "#f5fbf5";
      case "submitting":
        return "#fffdf5";
      default:
        return "#ffffff";
    }
  }

  function getStatusStyle(status: EntryStatus) {
    switch (status) {
      case "submitted":
        return {
          background: "#edf7ed",
          color: "#234b2c",
          border: "#b8d5bb",
        };
      case "failed":
      case "blocked":
      case "hidden":
        return {
          background: "#fff1f1",
          color: "#6b1f1f",
          border: "#e0b4b4",
        };
      case "submitting":
        return {
          background: "#fff8e8",
          color: "#6b4f1f",
          border: "#e7cf9a",
        };
      case "manual":
        return {
          background: "#eef4ff",
          color: "#1f3b5b",
          border: "#b7cbe3",
        };
      case "ready":
      default:
        return {
          background: "#F4F7F4",
          color: "#296735",
          border: "#D5DED8",
        };
    }
  }

  return (
    <div>
      <TitleButtons title="Suprimentos - Registro de Produto / Entrada no Estoque" />

      {notice && (
        <NoticeBanner notice={notice} onClose={() => setNotice(null)} />
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: "12px",
          marginBottom: "12px",
        }}
      >
        <div
          style={{
            border: "1px solid #cfd4da",
            background: "#f7f8fa",
            padding: "10px 14px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: "58px",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.8px",
              color: "#6c757d",
              textTransform: "uppercase",
              marginBottom: "4px",
            }}
          >
            Itens no lote
          </div>

          <div
            style={{
              fontSize: "15px",
              fontWeight: 700,
              color: "#212529",
              lineHeight: 1,
            }}
          >
            {summary.totalItens}
          </div>
        </div>

        <div
          style={{
            border: "1px solid #cfd4da",
            background: "#fff8f8",
            padding: "10px 14px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: "58px",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.8px",
              color: "#8a4b4b",
              textTransform: "uppercase",
              marginBottom: "4px",
            }}
          >
            Itens bloqueados no lote
          </div>

          <div
            style={{
              fontSize: "15px",
              fontWeight: 700,
              color: "#6b1f1f",
              lineHeight: 1,
            }}
          >
            {summary.totalItensBloqueados}
          </div>
        </div>

        <div
          style={{
            border: "1px solid #cfd4da",
            background: "#f7f8fa",
            padding: "10px 14px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: "58px",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.8px",
              color: "#6c757d",
              textTransform: "uppercase",
              marginBottom: "4px",
            }}
          >
            Qtd. total
          </div>

          <div
            style={{
              fontSize: "15px",
              fontWeight: 700,
              color: "#212529",
              lineHeight: 1,
            }}
          >
            {summary.totalQtd}
          </div>
        </div>

        <div
          style={{
            border: "1px solid #cfd4da",
            background: "#fff8f8",
            padding: "10px 14px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: "58px",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.8px",
              color: "#8a4b4b",
              textTransform: "uppercase",
              marginBottom: "4px",
            }}
          >
            Qtd. bloqueada a ser ignorada
          </div>

          <div
            style={{
              fontSize: "15px",
              fontWeight: 700,
              color: "#6b1f1f",
              lineHeight: 1,
            }}
          >
            {summary.totalQtdBloqueada}
          </div>
        </div>
      </div>
      <div
        style={{
          border: "1px solid #cfd4da",
          background: "#f7f8fa",
          padding: 12,
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 160px",
            gap: 8,
            alignItems: "end",
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 6,
              }}
            >
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: "#374151",
                }}
              >
                Código do Produto
              </label>
            </div>

            <input
              ref={scanInputRef}
              type="text"
              value={scanCode}
              onChange={handleScanInputChange}
              onKeyDown={handleScanInputKeyDown}
              onPaste={handleScanInputPaste}
              placeholder="BIPAR OU DIGITAR CÓDIGO"
              autoComplete="off"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              style={{
                width: "100%",
                height: 38,
                padding: "0 12px",
                border: "1px solid #cfd4da",
                background: "#ffffff",
                fontSize: 13,
                textTransform: "uppercase",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>

          <button
            type="button"
            onClick={handleAddCode}
            disabled={loadingCode}
            style={{
              height: 38,
              border: "1px solid #cfd4da",
              background: "#12417e",
              cursor: loadingCode ? "default" : "pointer",
              fontSize: 12,
              fontWeight: 700,
              textTransform: "uppercase",
              color: "#ffffff",
            }}
          >
            {loadingCode ? "CARREGANDO..." : "ADICIONAR"}
          </button>
        </div>
      </div>

      <div
        style={{
          border: "1px solid #cfd4da",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "130px 1fr 100px 140px 90px 260px 110px",
            background: "#eef1f4",
            borderBottom: "1px solid #cfd4da",
            fontSize: 12,
            fontWeight: 700,
            color: "#2f3a45",
            textTransform: "uppercase",
          }}
        >
          <div
            style={{ padding: "10px 12px", borderRight: "1px solid #d9dde2" }}
          >
            Código
          </div>
          <div
            style={{ padding: "10px 12px", borderRight: "1px solid #d9dde2" }}
          >
            Nome
          </div>
          <div
            style={{ padding: "10px 12px", borderRight: "1px solid #d9dde2" }}
          >
            Quantidade
          </div>
          <div
            style={{ padding: "10px 12px", borderRight: "1px solid #d9dde2" }}
          >
            Custo Unitário
          </div>
          <div
            style={{ padding: "10px 12px", borderRight: "1px solid #d9dde2" }}
          >
            Moeda
          </div>
          <div
            style={{ padding: "10px 12px", borderRight: "1px solid #d9dde2" }}
          >
            Status
          </div>
          <div style={{ padding: "10px 12px" }}>Ações</div>
        </div>

        {!items.length && (
          <div
            style={{
              padding: 18,
              fontSize: 13,
              color: "#6b7280",
              background: "#fff",
            }}
          >
            NENHUM ITEM ADICIONADO
          </div>
        )}

        {items.map((item) => {
          const locked =
            item.isOculto ||
            item.isBlockedByDept ||
            item.status === "submitting" ||
            item.status === "submitted";

          const statusStyle = getStatusStyle(item.status);

          return (
            <div
              key={item.id}
              style={{
                borderTop: "1px solid #e3e6ea",
                background: getRowBackground(item),
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "130px 1fr 100px 140px 90px 260px 110px",
                  alignItems: "stretch",
                }}
              >
                <div
                  style={{
                    padding: 12,
                    borderRight: "1px solid #eef0f2",
                    display: "flex",
                    alignItems: "center",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#1f2937",
                  }}
                >
                  {item.codigo}
                </div>

                <div style={{ padding: 8, borderRight: "1px solid #eef0f2" }}>
                  <TextField
                    label=""
                    value={item.nome}
                    onChange={(v) => updateItem(item.id, { nome: v ?? "" })}
                    placeholder="NOME DO PRODUTO"
                    uppercase
                    locked={locked}
                  />
                </div>

                <div style={{ padding: 8, borderRight: "1px solid #eef0f2" }}>
                  <TextField
                    label=""
                    value={item.quantidade}
                    onChange={(v) =>
                      updateItem(item.id, { quantidade: v ?? "" })
                    }
                    placeholder="QTD"
                    locked={locked}
                  />
                </div>

                <div style={{ padding: 8, borderRight: "1px solid #eef0f2" }}>
                  <TextField
                    label=""
                    value={item.custo}
                    onChange={(v) => updateItem(item.id, { custo: v ?? "" })}
                    placeholder="0,00"
                    contabil
                    currency={item.moeda}
                    currencyLocale={getCurrencyLocale(item.moeda)}
                    locked={locked}
                  />
                </div>

                <div style={{ padding: 8, borderRight: "1px solid #eef0f2" }}>
                  <DropField
                    label=""
                    value={item.moeda}
                    onChange={(v) =>
                      updateItem(item.id, {
                        moeda: ((v as AllowedCurrency) ??
                          "BRL") as AllowedCurrency,
                      })
                    }
                    options={opcoesMoeda}
                    placeholder="MOEDA"
                    uppercase
                    locked={locked}
                  />
                </div>

                <div
                  style={{
                    padding: 8,
                    borderRight: "1px solid #eef0f2",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      minHeight: 36,
                      display: "flex",
                      alignItems: "center",
                      padding: "6px 8px",
                      border: `1px solid ${statusStyle.border}`,
                      background: statusStyle.background,
                      color: statusStyle.color,
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      lineHeight: 1.25,
                      boxSizing: "border-box",
                    }}
                    title={item.statusText}
                  >
                    {item.statusText || item.status}
                  </div>
                </div>

                <div
                  style={{
                    padding: 8,
                    display: "grid",
                    gridTemplateRows: "36px 36px",
                    gap: 8,
                    alignContent: "center",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => toggleExpanded(item.id)}
                    style={{
                      width: "100%",
                      height: 36,
                      border: "1px solid #cfd4da",
                      background: "#ffffff",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                      textTransform: "uppercase",
                    }}
                  >
                    {item.expanded ? "Ocultar" : "Detalhes"}
                  </button>

                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    disabled={item.status === "submitting"}
                    style={{
                      width: "100%",
                      height: 36,
                      border: "1px solid #cfd4da",
                      background:
                        item.status === "submitting" ? "#f3f4f6" : "#ffffff",
                      cursor:
                        item.status === "submitting" ? "default" : "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      color: "#7f1d1d",
                    }}
                  >
                    Remover
                  </button>
                </div>
              </div>

              {item.expanded && (
                <div
                  style={{
                    borderTop: "1px solid #e5e7eb",
                    background: "#fafbfc",
                    padding: 12,
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1.2fr 1fr 1fr 1fr 1fr",
                      gap: 12,
                    }}
                  >
                    <TextField
                      label="NCM"
                      value={item.ncm}
                      onChange={(v) => updateItem(item.id, { ncm: v ?? "" })}
                      placeholder="NCM"
                      uppercase
                      locked={locked}
                    />

                    <DropField
                      label="Classificação"
                      value={item.classificacao}
                      onChange={(v) =>
                        updateItem(item.id, {
                          classificacao: (v ?? "").toString().toUpperCase(),
                        })
                      }
                      options={opcoesClassificacao}
                      placeholder="CLASSIFICAÇÃO"
                      uppercase
                      locked={locked}
                    />

                    <DropField
                      label="Posição"
                      value={item.posicao}
                      onChange={(v) =>
                        updateItem(item.id, { posicao: (v ?? "").toString() })
                      }
                      options={opcoesPosicao}
                      placeholder="POSIÇÃO"
                      uppercase
                      locked={locked}
                    />

                    <TextField
                      label="Dimensão"
                      value={item.dimensao}
                      onChange={(v) =>
                        updateItem(item.id, { dimensao: v ?? "" })
                      }
                      placeholder="EX: 10X20X30 CM"
                      uppercase
                      locked={locked}
                    />

                    <TextField
                      label="Peso"
                      value={item.peso}
                      onChange={(v) => updateItem(item.id, { peso: v ?? "" })}
                      placeholder="EX: 0,250 KG"
                      uppercase
                      locked={locked}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting || !items.length}
        style={{
          width: "100%",
          height: 42,
          marginTop: 16,
          border: "1px solid #cfd4da",
          background: submitting || !items.length ? "#9ca3af" : "#12417e",
          color: "#ffffff",
          fontSize: 13,
          fontWeight: 700,
          textTransform: "uppercase",
          cursor: submitting || !items.length ? "default" : "pointer",
        }}
      >
        {submitting ? "REGISTRANDO..." : "REGISTRAR"}
      </button>
    </div>
  );
};

export default RegistrarProdutoEntradaEstoque;
